/* eslint-disable no-shadow */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import { useFormik } from 'formik';
import { debounce, head } from 'lodash';
import filter from 'lodash/filter';
import find from 'lodash/find';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import moment from 'moment';
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { useCheckLicense } from './hooks/useCheckLicense';
import { useConfigurations } from './hooks/useConfigurations';
import { useLastDeployConfig } from './hooks/useLastDeployConfig';
import { useLicenseStatus } from './hooks/useLicenseStatus';
import { useMultiInstances } from './hooks/useMultiInstances';
import { usePipelineEngineConfig } from './hooks/usePipelineEngineConfig';
import { usePipelineOptions } from './hooks/usePipelineOptions';
import { usePipelineProjectName } from './hooks/usePipelineProjectName';
import { usePipelineVersions } from './hooks/usePipelineVersions';
import { useReplicaLimits } from './hooks/useReplicaLimits';
import { useValidatePipeline } from './hooks/useValidatePipeline';
import resolveDeployEngineOptions from './resolveDeployEngineOptions';
import {
  createObjectOption,
  createObjectPipelineVersion,
  createObjectViewDeploy,
  normalizeNamePipeline,
  normalizePipelines,
  normalizeSize
} from './utils/deployPipelineTransforms';
import { useCheckV3Eligibility } from '../../hooks/useCheckV3Eligibility';
import { useIsAutoscaling } from '../../hooks/useIsAutoscaling';
import { useDeployUiStore } from '../../stores/deployUiStore';
import { isAutoScalingDeploy } from '../../utils/isAutoScalingDeploy';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import i18n from '~/common/helpers/i18n';
import withHooks from '~/common/hoc/withHooks';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import { useTriggers } from '~/common/hooks/useTriggers';
import { findTriggerBySpec } from '~/common/utils/findTriggerBySpec';

const PipelineSchema = yup.object().shape({
  isReplicas: yup.boolean(),
  isPipelineEngine: yup.boolean(),
  pipeline: yup.object().shape({
    value: yup.string().required('common.messages.error.required_field'),
    label: yup.string().required('common.messages.error.required_field')
  }),
  version: yup.object().shape({
    value: yup.string().required('common.messages.error.required_field'),
    label: yup.string().required('common.messages.error.required_field')
  }),
  replicasDeploy: yup.object().when('isReplicas', (isReplicas, schema) => {
    if (isReplicas) {
      return schema.shape({
        value: yup.string().required('common.messages.error.required_field'),
        label: yup.string().required('common.messages.error.required_field')
      });
    }
    return schema.shape({
      value: yup.string(),
      label: yup.string()
    });
  }),
  configuration: yup.string().required('common.messages.error.required_field'),
  replicas: yup
    .number()
    .required(i18n.t('common.messages.error.target_replicas_are_required'))
    .transform(value => (Number.isNaN(value) ? 0 : value))
    .min(1, i18n.t('common.messages.error.target_replicas_minimum'))
    .when('isAutoScaling', {
      is: true,
      then: yup
        .number()
        .min(1, i18n.t('common.messages.error.max_replicas_minimum'))
        .required(i18n.t('common.messages.error.max_replicas_are_required'))
        .test(
          'is-greater-than-minReplicas',
          i18n.t('label.max_replicas_error'),
          function (value) {
            return value > this.parent.minReplicas;
          }
        )
        .when('replicaLimits', {
          is: replicaLimits => typeof replicaLimits === 'object',
          then: yup.number().test({
            name: 'check-replica-limits-autoscaling',
            test(value) {
              const { replicaLimits } = this.parent;
              const maxAllowed = replicaLimits?.max;

              if (!maxAllowed) return true;
              return value <= maxAllowed
                ? true
                : this.createError({
                    message: i18n.t(
                      'common.messages.error.max_replicas_maximum',
                      {
                        maxAllowedValue: maxAllowed
                      }
                    ),
                    path: 'replicas'
                  });
            }
          }),
          otherwise: yup.number()
        }),
      otherwise: yup
        .number()
        .min(1, i18n.t('common.messages.error.target_replicas_minimum'))
        .when('replicaLimits', {
          is: replicaLimits => typeof replicaLimits === 'object',
          then: yup.number().test({
            name: 'check-replica-limits',
            test(value) {
              const { replicaLimits } = this.parent;
              const maxAllowed = replicaLimits?.target;

              if (!maxAllowed) return true;
              return value <= maxAllowed
                ? true
                : this.createError({
                    message: i18n.t(
                      'common.messages.error.target_replicas_maximum',
                      {
                        maxAllowedValue: maxAllowed
                      }
                    ),
                    path: 'replicas'
                  });
            }
          }),
          otherwise: yup.number()
        })
    }),

  minReplicas: yup
    .number()
    .transform(value => (Number.isNaN(value) ? 0 : value))
    .when('isAutoScaling', {
      is: true,
      then: yup
        .number()
        .required(i18n.t('common.messages.error.min_replicas_are_required'))
        .min(0, i18n.t('common.messages.error.min_replicas_minimum'))
        .when('replicaLimits', {
          is: replicaLimits => !!replicaLimits,
          then: yup.number().test({
            name: 'check-min-replica-limits',
            test(value) {
              const { replicaLimits } = this.parent;
              const maxAllowed = replicaLimits?.min;

              if (!maxAllowed) return true;
              return value <= maxAllowed
                ? true
                : this.createError({
                    message: i18n.t(
                      'common.messages.error.min_replicas_maximum',
                      {
                        maxAllowedValue: maxAllowed
                      }
                    ),
                    path: 'minReplicas'
                  });
            }
          }),
          otherwise: yup.number()
        })
        .test(
          'is-less-than-maxReplicas',
          i18n.t('label.min_replicas_error'),
          function (value) {
            return value < this.parent.replicas;
          }
        ),
      otherwise: yup.number().notRequired()
    }),
  cooldownPeriod: yup.number().when(['isAutoScaling', 'minReplicas'], {
    is: (isAutoScaling, minReplicas) => isAutoScaling && minReplicas === 0,
    then: yup
      .number()
      .required(i18n.t('scenes.runtime.labels.cooldown_time_required'))
      .min(60, i18n.t('scenes.runtime.labels.cooldown_time_minimum'))
      .max(300, i18n.t('scenes.runtime.labels.cooldown_time_maximum')),
    otherwise: yup.number().notRequired()
  }),
  consumer: yup.number().required('Consumer is required'),
  replicaLimits: yup.object().shape({
    min: yup.number(),
    max: yup.number(),
    target: yup.number()
  })
});

const enhancer = withHooks(props => {
  const [projectId, setProjectId] = useState(null);
  const [configurations, setConfigurations] = useState(null);
  const [pipelineInstances, setPipelineInstences] = useState(null);
  const [validationLicense, setValidationLicense] = useState([]);
  const [validationPipeline, setValidationPipeline] = useState([]);
  const [configurationLastDeploy, setConfigurationLastDeploy] = useState(null);
  const [sliderProps, setSliderProps] = useState({ max: 0 });
  const [openPromote, setOpenPromote] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [newDeploy, setNewDeploy] = useState();
  const [loadingPromote, setLoadingPromote] = useState(false);
  const [environment, setEnvironment] = useState(props.environment);
  const [versionManuallyChanged, setVersionManuallyChanged] = useState(false);
  const [lastValidCooldownPeriod, setLastValidCooldownPeriod] = useState(60);
  const deploymentSelect = props.showPromote ? props.deploymentSelected : false;
  const deployment = deploymentSelect || null;
  const token = useSelector(state => state?.authentication?.userData?.token);
  const [newDeployment, setNewDeployment] = useState(deployment);
  const [pipeline, setPipeline] = useState(deploymentSelect?.pipeline);
  const [isOpenAdvancedSettings, setIsOpenAdvancedSettings] = useState(false);
  const [isBetaEngineV3Eligible, setIsBetaEngineV3Eligible] = useState(false);
  const [isBetaEngineV3Enabled, setIsBetaEngineV3Enabled] = useState(false);
  const [triggerSpec, setTriggerSpec] = useState(null);
  const application = useSelector(state => state.application);
  const showInactiveTriggersExperience = useIsAutoscaling();
  const { mutateAsync: checkV3Eligibility } = useCheckV3Eligibility();
  const triggers = useTriggers(application.activeRealm);

  const handleToggle = () => setIsOpenAdvancedSettings(!isOpenAdvancedSettings);

  const pipelineRedeploy = useDeployUiStore(state => state.pipelineToRedeploy);

  const { RUN_SET_ENGINE_VERSION, RUN_BETA_ENGINE_V3 } = featureFlagConstants;
  const { treatments } = useFeatureFlag(
    [RUN_SET_ENGINE_VERSION, RUN_BETA_ENGINE_V3],
    {
      realm: props?.realm
    }
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const isPipelineEngine =
    treatments[RUN_SET_ENGINE_VERSION].treatment === 'on';
  const isBetaEngineV3FeatureEnabled =
    treatments[RUN_BETA_ENGINE_V3].treatment === 'on';

  const texts = {
    deploy: {
      title: 'scenes.runtime.labels.deploy_pipeline',
      button: 'common.actions.deploy',
      modalConfirmation: {
        title: 'label.deploy_pipeline_prompt',
        text: 'label.deploy_pipeline_msg',
        confirmText: 'common.actions.deploy'
      },
      datatestidText: 'run-confirm-deploy'
    },
    redeploy: {
      title: 'label.redeploy_pipeline',
      button: 'action.redeploy',
      modalConfirmation: {
        title: 'label.redeploy_pipeline_prompt',
        text: 'label.redeploy_pipeline_msg',
        confirmText: 'action.redeploy'
      },
      datatestidText: 'run-confirm-redeploy'
    },
    promote: {
      title: 'label.pipeline_promotion_configuration',
      button: 'action.promote',
      datatestidText: 'run-confirm-promote'
    }
  };

  const currentExecution = maxExecution => {
    if (triggerSpec?.type !== 'scheduler') {
      return maxExecution;
    }

    if (!triggerSpec?.concurrentScheduling) {
      return 1;
    }

    return maxExecution;
  };

  const resetBetaEngineV3Eligibility = () => {
    setIsBetaEngineV3Eligible(false);
    setIsBetaEngineV3Enabled(false);
  };

  const updateBetaEngineV3Eligibility = async pipelineId => {
    resetBetaEngineV3Eligibility();

    if (!isBetaEngineV3FeatureEnabled || !pipelineId || props.showPromote) {
      return;
    }

    try {
      const response = await checkV3Eligibility({
        realm: props.realm,
        pipelineId
      });
      const eligible = !!response?.eligible;

      setIsBetaEngineV3Eligible(eligible);
    } catch (e) {
      resetBetaEngineV3Eligibility();
    }
  };

  const trigger = findTriggerBySpec(triggers, pipeline?.triggerSpec);

  const mapValueSchema = (values = {}, schema = []) => {
    const keys = Object.keys(values);
    return keys.reduce((acc, current) => {
      const prop = schema.find(x => x.property === current);
      if (prop) {
        acc.push({
          label: prop.label,
          value: JSON.stringify(values[current])
        });
        return acc;
      }
      return acc;
    }, []);
  };
  const triggerSource = mapValueSchema(
    pipeline?.triggerSpec,
    head(trigger?.jsonSchema)?.schema
  );

  const deployType = props.showPromote
    ? 'promote'
    : pipelineRedeploy
      ? 'redeploy'
      : 'deploy';

  const { validatePipeline: fetchValidatePipeline } = useValidatePipeline();
  const { fetchLicenseStatus } = useLicenseStatus();
  const { checkLicense: fetchCheckLicense } = useCheckLicense();
  const { fetchConfigurations } = useConfigurations();
  const { fetchMultiInstances } = useMultiInstances();
  const { fetchPipelineProjectName } = usePipelineProjectName();
  const { fetchPipelineOptions } = usePipelineOptions();
  const { fetchPipelineVersions } = usePipelineVersions();
  const { fetchLastDeployConfig } = useLastDeployConfig();

  const form = useFormik({
    initialValues: {
      isPipelineEngine: true,
      isReplicas: false,
      pipeline: { value: '', label: '' },
      version: { value: '', label: '' },
      replicasDeploy: { value: '', label: '' },
      projectName: '',
      configuration: null,
      replicas: 1,
      consumer: sliderProps.max,
      minReplicas: 0,
      cooldownPeriod: 60,
      isAutoScaling: showInactiveTriggersExperience,
      replicaLimits: null
    },
    validationSchema: PipelineSchema,
    onSubmit: async values => {
      try {
        const validationResult = await fetchValidatePipeline({
          pipelineId: values?.version?.value,
          environment: props.environment,
          realm: props?.realm
        });

        const hasValidationErrors = validationResult?.analysis?.some(a =>
          a?.details?.some(detail => detail?.level === 'ERROR')
        );

        if (hasValidationErrors) {
          setValidationPipeline(validationResult?.analysis);
          form.setSubmitting(false);
          return;
        }

        const config = find(
          configurations,
          item => normalizeSize(item.name) === values?.configuration
        );
        const size = normalizeSize(config?.name);

        const environment = props.showPromote
          ? values.environment?.value
          : props.environment;

        const newDeploy = {
          configuration: {
            id: config?.id,
            size,
            consumers: values?.consumer
          },
          pipelineId: values?.version?.value,
          force: !!configurationLastDeploy,
          replicas: values?.replicas,
          minReplicas: values?.isAutoScaling
            ? values?.minReplicas
            : values?.replicas,
          environment,
          realm: props?.realm,
          consumer: values?.consumer,
          replicaInstanceName: values?.replicasDeploy?.value,
          type: deployType,
          ...(isBetaEngineV3FeatureEnabled
            ? {
                engineVersion: resolveDeployEngineOptions({
                  isBetaEngineV3Eligible,
                  isBetaEngineV3Enabled,
                  legacyEngineSelectorEnabled: isPipelineEngine,
                  formIsPipelineEngine: values?.isPipelineEngine
                })
              }
            : {
                isPipelineEngine: !isPipelineEngine || values?.isPipelineEngine
              })
        };

        newDeploy.cooldownPeriod =
          values?.isAutoScaling && values?.minReplicas === 0
            ? values?.cooldownPeriod || 60
            : null;

        const pipelineLastVersion = find(
          pipeline?.versionMinor,
          item => item.id === values?.version?.value
        );

        const licenseStatus = await fetchLicenseStatus({
          environment,
          realm: props.realm,
          replicas: values?.replicas,
          pipelineId: values?.version?.value,
          size
        });

        const objViewDeploy = createObjectViewDeploy({
          pipeline: {
            ...deployment?.pipeline,
            ...pipelineLastVersion,
            triggerSpec: {
              ...pipelineLastVersion,
              ...pipeline.triggerSpec
            }
          },
          activeConfiguration: {
            ...config,
            replicaCount: values?.replicas,
            minReplicaCount: values?.isAutoScaling
              ? values?.minReplicas
              : values?.replicas
          },
          deploymentStatus: deployment?.deploymentStatus,
          licenses: pipeline?.licenses,
          replicas: values?.replicas,
          minReplicas: values?.isAutoScaling
            ? values?.minReplicas
            : values?.replicas,
          startTime: moment().valueOf(),
          force: !!configurationLastDeploy,
          licenseTotal: pipeline?.licenseTotal,
          parameterizedReplica: values?.replicasDeploy?.value,
          consumers: values?.consumer,
          licenseStatus
        });

        if (props.showPromote) {
          setNewDeployment({
            deployment: {
              ...objViewDeploy,
              newEnvironment: environment
            }
          });
          setOpenPromote(true);
        } else {
          setNewDeploy({
            ...newDeploy,
            environment,
            deployment: objViewDeploy,
            texts: texts[deployType].modalConfirmation,
            isRedeploy: pipelineRedeploy,
            projectId,
            projectName: values?.projectName,
            datatestidText: 'button-confirm-redeploy'
          });
          setShowConfirmDialog(true);
        }
      } catch (e) {
        console.error('Error in onSubmit:', e);
        form.setErrors({ pipeline: 'An error occurred' });
        form.setSubmitting(false);
      }
    }
  });

  const { data: pipelineEngineConfig } = usePipelineEngineConfig({
    realm: props.realm,
    environment: props.environment
  });

  const { data: replicaLimits, isLoading: loadingReplicaLimits } =
    useReplicaLimits({
      realm: props.realm,
      environment,
      token
    });

  const setConfigAndReplicas = async ({
    pipelineName,
    versionMajor,
    replicaName
  }) => {
    try {
      const { data } = await fetchLastDeployConfig({
        realm: props.realm,
        environment: props.environment,
        pipelineName: normalizeNamePipeline(pipelineName, replicaName),
        versionMajor
      });
      setConfigurationLastDeploy(data?.deploymentByNameAndVersionMajor);
      if (data?.deploymentByNameAndVersionMajor) {
        setSliderProps({
          max: data.deploymentByNameAndVersionMajor.activeConfiguration
            ?.consumers
        });

        if (data.deploymentByNameAndVersionMajor.replicaName)
          form.setFieldValue('replicasDeploy', {
            value: data.deploymentByNameAndVersionMajor.replicaName,
            label: data.deploymentByNameAndVersionMajor.replicaName
          });

        if (
          isAutoScalingDeploy({
            minReplicaCount:
              data.deploymentByNameAndVersionMajor.activeConfiguration
                ?.minReplicaCount,
            replicaCount:
              data.deploymentByNameAndVersionMajor.activeConfiguration
                ?.replicaCount
          }) &&
          showInactiveTriggersExperience
        ) {
          const minReplicas = parseInt(
            data.deploymentByNameAndVersionMajor.activeConfiguration
              ?.minReplicaCount,
            10
          );
          const defaultMinReplicas =
            form.values?.replicaLimits?.min !== undefined
              ? form.values.replicaLimits.min
              : 0;
          form.setFieldValue(
            'minReplicas',
            !Number.isNaN(minReplicas) ? minReplicas : defaultMinReplicas
          );
          setTimeout(() => form.setFieldTouched('minReplicas', true));
          form.setFieldValue('isAutoScaling', true);

          if (!form.touched.cooldownPeriod) {
            const cooldownPeriod =
              data.deploymentByNameAndVersionMajor.activeConfiguration
                ?.cooldownPeriod;
            if (cooldownPeriod !== undefined && cooldownPeriod !== null) {
              form.setFieldValue('cooldownPeriod', cooldownPeriod);
              setLastValidCooldownPeriod(cooldownPeriod);
            } else {
              form.setFieldValue('cooldownPeriod', 60);
              setLastValidCooldownPeriod(60);
            }
          } else if (form.values.cooldownPeriod === '') {
            return;
          }
        } else {
          form.setFieldValue('isAutoScaling', false);
        }
        form.setFieldValue(
          'configuration',
          normalizeSize(
            data.deploymentByNameAndVersionMajor.activeConfiguration?.name
          )
        );
        form.setFieldValue(
          'consumer',
          data.deploymentByNameAndVersionMajor.activeConfiguration
            ?.actualConsumers
        );

        const replicas = parseInt(
          data.deploymentByNameAndVersionMajor.activeConfiguration
            ?.replicaCount,
          10
        );
        const defaultReplicas =
          form.values?.replicaLimits?.target !== undefined
            ? form.values.replicaLimits.target
            : 1;
        form.setFieldValue(
          'replicas',
          !Number.isNaN(replicas) ? replicas : defaultReplicas
        );

        const cooldownPeriod =
          data.deploymentByNameAndVersionMajor.activeConfiguration
            ?.cooldownPeriod;
        if (form.values.isAutoScaling && form.values.minReplicas === 0) {
          if (!form.touched.cooldownPeriod) {
            if (cooldownPeriod !== undefined && cooldownPeriod !== null) {
              form.setFieldValue('cooldownPeriod', cooldownPeriod);
            } else if (!props.showPromote) {
              form.setFieldValue('cooldownPeriod', 60);
            }
          }
        }
      }
    } catch (e) {
      setConfigurationLastDeploy(null);
    }
  };

  const addConfigurations = async ({ pipelineVersionId, environment }) => {
    setConfigurations(null);
    try {
      const { data } = await fetchConfigurations({
        pipelineVersionId,
        realm: props.realm,
        environment: environment || props.environment
      });
      setConfigurations(data?.configurations);
    } catch (e) {
      setConfigurations(null);
    }
  };

  const checkLicense = async variant => {
    if (variant?.size) {
      const size = normalizeSize(variant?.size);
      try {
        const { data } = await fetchCheckLicense({
          ...variant,
          size,
          realm: props.realm,
          environment: props.environment
        });
        setValidationLicense(data?.checkLicense?.analysis);
      } catch (e) {
        setValidationLicense([]);
      }
    }
  };

  const validatePipeline = async variant => {
    try {
      const data = await fetchValidatePipeline({
        ...variant,
        realm: props.realm
      });
      setValidationPipeline(data?.analysis);
    } catch (e) {
      setValidationPipeline([]);
    }
  };

  const setMultiInstances = async variant => {
    try {
      if (variant?.replicaName) {
        const { data } = await fetchMultiInstances({
          ...variant,
          realm: props.realm,
          environment: props.environment
        });
        setPipelineInstences(data?.replicaInstance);
      }
    } catch (e) {
      setPipelineInstences([]);
    }
  };

  const closePromote = () => {
    setOpenPromote(false);
  };

  const pipelineToPromoteConfirm = async deploymentData => {
    setLoadingPromote(true);
    const isPipelineEngineForm = form.values.isPipelineEngine;
    const newPayload = {
      configuration: {
        consumers: deploymentData.activeConfiguration.actualConsumers,
        id: deploymentData.activeConfiguration.id,
        size: normalizeSize(deploymentData.activeConfiguration.name)
      },
      environment: deploymentData.newEnvironment,
      force: true,
      pipelineId: deploymentData.pipeline.id,
      replicaInstanceName:
        deploymentData.parameterizedReplica ||
        form.values.replicasDeploy?.value,
      realm: props.realm,
      replicas: deploymentData.replicas,
      minReplicas: deploymentData.minReplicas,
      isPipelineEngine: !isPipelineEngine || isPipelineEngineForm
    };

    newPayload.cooldownPeriod =
      form.values.isAutoScaling && form.values.minReplicas === 0
        ? form.values.cooldownPeriod || 60
        : null;

    if (form.values.deletePipe) {
      props.remove({
        environment: deployment?.activeConfiguration?.environment?.name,
        realm: props.realm,
        deployment: deployment.id
      });
    }

    props.createDeploy(newPayload, null, true);

    const { project } = await fetchPipelineProjectName({
      id: deployment.pipeline.id,
      realm: props?.realm
    });

    props.changeEnvironment(deploymentData.newEnvironment);
    setOpenPromote(false);
    setLoadingPromote(false);
    props.close();

    const params = {
      projectId: project.id,
      refreshInterval: searchParams.get('refreshInterval') || '5'
    };

    const viewValue = searchParams.get('view');
    if (viewValue) {
      params.view = viewValue;
    }

    const pipelineNameValue = searchParams.get('pipelineName');
    if (pipelineNameValue != null && pipelineNameValue !== undefined) {
      params.pipelineName = pipelineNameValue;
    }

    setSearchParams(params);
  };

  const actions = {
    onAdvancedSettingsToggle: handleToggle,
    loadPipelines: async (search, _, { searchPage, page, previousSearch }) =>
      new Promise((resolve, reject) =>
        debounce(async () => {
          try {
            const request = await fetchPipelineOptions({
              realm: props?.realm,
              size: 10,
              search: {
                name: search,
                page: search
                  ? previousSearch === search
                    ? // eslint-disable-next-line no-plusplus, no-param-reassign
                      ++searchPage
                    : 0
                  : // eslint-disable-next-line no-plusplus, no-param-reassign
                    ++page
              }
            });
            const options = normalizePipelines(
              request?.data?.searchPipelines?.content
            );

            const hasMore = !request?.data?.searchPipelines?.last;

            resolve({
              options,
              hasMore,
              additional: {
                page: !search ? request?.data?.searchPipelines?.number : page,
                searchPage: search
                  ? previousSearch === search
                    ? request?.data?.searchPipelines?.number
                    : 0
                  : -1,
                previousSearch: search
              }
            });
          } catch (error) {
            reject();
          }
        }, 1000)()
      ),
    onChangePipeline: async optionPipline => {
      try {
        form.setFieldValue('pipeline', optionPipline);

        form.setFieldValue('configuration', null);

        form.setFieldValue('version', { label: '', value: '' });

        form.setFieldValue('projectName', '');

        setConfigurationLastDeploy(null);
        resetBetaEngineV3Eligibility();

        setConfigurations([]);

        setPipelineInstences([]);

        const current = await fetchPipelineVersions({
          realm: props?.realm,
          environment: props?.environment,
          id: optionPipline?.value
        });

        setTriggerSpec(current?.triggerSpec);

        setMultiInstances({
          replicaName: current?.parameterizedReplica
        });

        form.setFieldValue('isReplicas', !!current?.parameterizedReplica);
        let pipelineLastVersion = maxBy(
          filter(current?.versionMinor, item => !item.disabled),
          item => item.versionMinor
        );

        if (pipelineRedeploy) {
          pipelineLastVersion = pipelineRedeploy;
        }

        validatePipeline({
          pipelineId: pipelineLastVersion?.id,
          environment: props.environment
        });

        await addConfigurations({
          pipelineVersionId: pipelineLastVersion?.id
        });

        const { project } = await fetchPipelineProjectName({
          id: current.id,
          realm: props?.realm
        });

        setProjectId(project?.id);
        form.setFieldValue('projectName', project?.name);

        form.setFieldValue('version', {
          value: pipelineLastVersion?.id,
          label: `${pipelineLastVersion?.versionMajor}.${pipelineLastVersion?.versionMinor}`
        });

        form.setFieldValue(
          'isPipelineEngine',
          pipelineRedeploy ? pipelineLastVersion?.isPipelineEngine : true
        );

        await updateBetaEngineV3Eligibility(pipelineLastVersion?.id);

        if (props.showPromote) {
          setPipeline({
            ...props.deploymentSelected.pipeline,
            triggerSpec: current.triggerSpec
          });

          setSliderProps({
            max: props.deploymentSelected.activeConfiguration?.consumers
          });
          if (
            isAutoScalingDeploy({
              minReplicaCount:
                props.deploymentSelected?.activeConfiguration?.minReplicaCount,
              replicaCount:
                props.deploymentSelected.activeConfiguration.replicaCount
            }) &&
            showInactiveTriggersExperience
          ) {
            const minReplicas = parseInt(
              props.deploymentSelected.activeConfiguration?.minReplicaCount,
              10
            );
            form.setFieldValue(
              'minReplicas',
              !Number.isNaN(minReplicas) ? minReplicas : 0
            );
            setTimeout(() => form.setFieldTouched('minReplicas', true));
            form.setFieldValue('isAutoScaling', true);

            const cooldownPeriod =
              props.deploymentSelected.activeConfiguration?.cooldownPeriod;
            if (cooldownPeriod !== undefined && cooldownPeriod !== null) {
              form.setFieldValue('cooldownPeriod', cooldownPeriod);
              setLastValidCooldownPeriod(cooldownPeriod);
            } else {
              form.setFieldValue('cooldownPeriod', 60);
              setLastValidCooldownPeriod(60);
            }
          } else {
            form.setFieldValue('isAutoScaling', false);
          }
          const replicas = parseInt(
            props.deploymentSelected.activeConfiguration?.replicaCount,
            10
          );
          const defaultReplicas =
            form.values?.replicaLimits?.target !== undefined
              ? form.values.replicaLimits.target
              : 1;
          form.setFieldValue(
            'replicas',
            !Number.isNaN(replicas) ? replicas : defaultReplicas
          );

          const minReplicas = parseInt(
            props.deploymentSelected.activeConfiguration?.minReplicaCount,
            10
          );
          form.setFieldValue(
            'minReplicas',
            !Number.isNaN(minReplicas) ? minReplicas : 0
          );
          setTimeout(() => form.setFieldTouched('minReplicas', true));

          form.setFieldValue(
            'configuration',
            normalizeSize(props.deploymentSelected.activeConfiguration?.name)
          );
          form.setFieldValue(
            'consumer',
            props.deploymentSelected.activeConfiguration?.actualConsumers
          );

          form.setFieldValue('environment', {
            value: props.environment,
            label: props.environment
          });

          form.setFieldValue('deletePipe', false);

          if (
            showInactiveTriggersExperience &&
            form.values.isAutoScaling &&
            form.values.minReplicas === 0
          ) {
            const cooldownPeriod =
              props.deploymentSelected.activeConfiguration?.cooldownPeriod;
            if (cooldownPeriod !== undefined && cooldownPeriod !== null) {
              form.setFieldValue('cooldownPeriod', cooldownPeriod);
              setLastValidCooldownPeriod(cooldownPeriod);
            } else {
              form.setFieldValue('cooldownPeriod', 60);
              setLastValidCooldownPeriod(60);
            }
          }

          setPipeline(current);
          return;
        }

        if (showInactiveTriggersExperience) {
          form.setFieldValue('isAutoScaling', true);
          if (!props.showPromote && !form.touched.cooldownPeriod) {
            form.setFieldValue('cooldownPeriod', 60);
          }
        }

        if (!props.showPromote) {
          setConfigAndReplicas({
            pipelineName: pipelineLastVersion?.name,
            versionMajor: pipelineLastVersion?.versionMajor
          });
        }

        setPipeline(current);
      } catch (e) {
        setPipeline(null);
      }
    },
    onChangePipelineVersionEngine: engineVersion => {
      form.setFieldValue('isPipelineEngine', engineVersion.value);
    },
    onChangePipelineVersion: async optionPipelineVersion => {
      setVersionManuallyChanged(true);
      const currentReplicasDeploy = form.values.replicasDeploy;

      form.setFieldValue('version', optionPipelineVersion);

      setConfigurationLastDeploy(null);
      resetBetaEngineV3Eligibility();

      if (!props.showPromote) {
        form.setFieldValue('configuration', null);
      }

      if (
        isAutoScalingDeploy({
          minReplicaCount:
            props.deploymentSelected?.activeConfiguration.minReplicaCount,
          replicaCount:
            props.deploymentSelected?.activeConfiguration.replicaCount
        }) &&
        showInactiveTriggersExperience
      ) {
        const minReplicas = parseInt(
          props.deploymentSelected?.activeConfiguration?.minReplicaCount,
          10
        );
        form.setFieldValue(
          'minReplicas',
          !Number.isNaN(minReplicas) ? minReplicas : 0
        );
        setTimeout(() => form.setFieldTouched('minReplicas', true));
        form.setFieldValue('isAutoScaling', true);

        if (props.showPromote) {
          const cooldownPeriod =
            props.deploymentSelected.activeConfiguration?.cooldownPeriod;
          if (cooldownPeriod !== undefined && cooldownPeriod !== null) {
            form.setFieldValue('cooldownPeriod', cooldownPeriod);
            setLastValidCooldownPeriod(cooldownPeriod);
          } else {
            form.setFieldValue('cooldownPeriod', 60);
            setLastValidCooldownPeriod(60);
          }
        } else if (!form.touched.cooldownPeriod) {
          form.setFieldValue('cooldownPeriod', 60);
        }
      } else {
        form.setFieldValue('isAutoScaling', false);
      }

      form.setFieldValue(
        'replicas',
        props.deploymentSelected?.activeConfiguration.replicaCount
      );

      setTimeout(() => form.setFieldTouched('replicas', true));

      form.setFieldValue('projectName', '');

      setConfigurations([]);

      setValidationPipeline([]);

      const pipelineLastVersion = find(
        pipeline?.versionMinor,
        item => item.id === optionPipelineVersion.value
      );

      form.setFieldValue(
        'isReplicas',
        !!pipelineLastVersion?.parameterizedReplica ||
          !!currentReplicasDeploy?.value
      );

      await addConfigurations({
        pipelineVersionId: optionPipelineVersion.value
      });
      if (pipelineLastVersion?.id) {
        await validatePipeline({
          pipelineId: pipelineLastVersion?.id,
          environment: props.environment
        });
      }
      await updateBetaEngineV3Eligibility(
        pipelineLastVersion?.id || optionPipelineVersion?.value
      );
      setMultiInstances({
        replicaName: pipelineLastVersion?.parameterizedReplica
      });
      if (pipelineLastVersion?.name && !props.showPromote) {
        setConfigAndReplicas({
          pipelineName: pipelineLastVersion?.name,
          versionMajor: pipelineLastVersion?.versionMajor,
          replicaName:
            currentReplicasDeploy?.value ||
            pipelineLastVersion?.parameterizedReplica
        });
      }

      const { project } = await fetchPipelineProjectName({
        id: pipeline.id,
        realm: props?.realm,
        pipelineMajorVersion: parseInt(optionPipelineVersion.label, 10)
      });

      form.setFieldValue('projectName', project?.name);
    },
    onChangeConfiguration: async (
      _,
      { pipelineId, replicas, value, replicasDeploy }
    ) => {
      form.setFieldValue('configuration', value);
      const config = find(
        configurations,
        item => normalizeSize(item.name) === value
      );

      checkLicense({
        size: config?.name,
        pipelineId,
        replicas,
        replicaInstanceName: replicasDeploy.value
      });

      setSliderProps({
        max: config.consumers
      });

      form.setFieldValue('consumer', currentExecution(config.consumers));

      setTimeout(async () => {
        await form.validateForm();
      }, 0);
    },
    onChangeConsumer: async (_, value) => {
      form.setFieldValue('consumer', value);
      form.setFieldTouched('consumer', true);
    },
    onChangeReplicasDeploy: async ({
      pipelineId,
      configurationId,
      replicas,
      replicasDeploy
    }) => {
      form.setFieldValue('replicasDeploy', replicasDeploy);
      form.setFieldValue('configuration', null);

      const pipelineLastVersion = maxBy(
        filter(pipeline?.versionMinor, item => !item.disabled),
        item => item?.versionMinor
      );
      await addConfigurations({
        pipelineVersionId: pipelineLastVersion?.id
      });

      const config = find(
        configurations,
        item => normalizeSize(item.name) === configurationId
      );

      checkLicense({
        pipelineId,
        size: config?.name,
        replicas,
        replicaInstanceName: replicasDeploy.value
      });

      setConfigAndReplicas({
        pipelineName: pipelineLastVersion?.name,
        versionMajor: pipelineLastVersion?.versionMajor,
        replicaName: replicasDeploy.value
      });
    },
    onChangeMinReplicas: ({ value }) => {
      const parsedValue = parseInt(value, 10);
      const previousMinReplicas = form.values.minReplicas;
      form.setFieldValue('minReplicas', parsedValue);

      if (
        previousMinReplicas === 0 &&
        parsedValue > 0 &&
        form.values.isAutoScaling
      ) {
        if (form.values.cooldownPeriod && form.values.cooldownPeriod !== '') {
          setLastValidCooldownPeriod(form.values.cooldownPeriod);
        }
      }

      if (parsedValue === 0 && form.values.isAutoScaling) {
        form.setFieldValue('cooldownPeriod', lastValidCooldownPeriod);
        setTimeout(() => {
          form.validateField('minReplicas');
          form.validateField('replicas');
        }, 0);
      }
    },
    onChangecooldownPeriod: ({ value }) => {
      if (value === '') {
        form.setFieldValue('cooldownPeriod', '');
        form.setFieldTouched('cooldownPeriod', true, false);
        return;
      }

      const parsedValue = parseInt(value, 10);
      if (!Number.isNaN(parsedValue)) {
        form.setFieldValue('cooldownPeriod', parsedValue);
        form.setFieldTouched('cooldownPeriod', true, false);
        setLastValidCooldownPeriod(parsedValue);
      }
    },
    onChangeReplicas: ({
      pipelineId,
      configurationId,
      replicasDeploy,
      value
    }) => {
      const parsedValue =
        value === '' || value === null ? undefined : Number(value);
      form.setFieldValue('replicas', parsedValue);
      form.setFieldTouched('replicas', true, false);
      setTimeout(() => {
        form.validateField('replicas');
        form.validateForm();
      }, 0);
      const config = find(
        configurations,
        item => normalizeSize(item.name) === configurationId
      );
      checkLicense({
        pipelineId,
        size: config?.name,
        replicas: parsedValue,
        replicaInstanceName: replicasDeploy.value
      });
    },
    onChangeDeletePipe: value => {
      form.setFieldValue('deletePipe', value);
    },
    onChangeEnvironment: async ({ newEnv, replicas, size }) => {
      const licenseStatus = await fetchLicenseStatus({
        environment: newEnv.value,
        realm: props.realm,
        replicas,
        pipelineId: pipeline.id,
        size
      });
      form.setFieldValue('environment', newEnv);
      setEnvironment(newEnv.value);
      setPipeline({
        ...pipeline,
        licenseStatus
      });
      await addConfigurations({
        pipelineVersionId: pipeline.id,
        environment: newEnv.value
      });
    },
    onChangeIsAutoScaling: ({ value }) => {
      const previousAutoScaling = form.values.isAutoScaling;
      form.setFieldValue('isAutoScaling', value);

      if (!previousAutoScaling && value && form.values.minReplicas === 0) {
        form.setFieldValue('cooldownPeriod', lastValidCooldownPeriod);
        setTimeout(() => {
          form.validateField('minReplicas');
          form.validateField('replicas');
        }, 0);
      }

      if (previousAutoScaling && !value) {
        if (form.values.cooldownPeriod && form.values.cooldownPeriod !== '') {
          setLastValidCooldownPeriod(form.values.cooldownPeriod);
        }
      }
    },
    onChangeBetaEngineV3Enabled: event => {
      setIsBetaEngineV3Enabled(event.target.checked);
    }
  };
  useEffect(() => {
    if (!deployment) return;
    const initial = async () => {
      setVersionManuallyChanged(false);
      await actions.onChangePipeline(createObjectOption(deployment));
      await actions.onChangePipelineVersion(
        createObjectPipelineVersion(deployment?.pipeline)
      );
    };

    initial();
  }, [deployment]);

  useEffect(() => {
    if (pipelineEngineConfig === undefined) return;
    form.setFieldValue('isPipelineEngine', pipelineEngineConfig);
  }, [pipelineEngineConfig]);

  useEffect(() => {
    if (replicaLimits === undefined) return;
    form.setFieldValue('replicaLimits', replicaLimits);
  }, [replicaLimits]);

  useEffect(() => {
    const setPipeline = async () => {
      const pipelineValue = {
        label: pipelineRedeploy.name,
        value: pipelineRedeploy.id
      };
      form.setFieldValue('isPipelineEngine', pipelineRedeploy.isPipelineEngine);
      await actions.onChangePipeline(pipelineValue);
    };

    if (pipelineRedeploy) {
      setPipeline();
    }
  }, [pipelineRedeploy]);

  useEffect(() => {
    if (versionManuallyChanged && form.isValid) {
      setVersionManuallyChanged(false);
    }
  }, [form.isValid, versionManuallyChanged]);

  const versionMinor = map(pipeline?.versionMinor, item => ({
    label: `${item?.versionMajor}.${item?.versionMinor}`,
    value: item?.id
  }));

  const optionVersionEngine = [
    {
      label: i18n.t('label.deprecated_V1'),
      value: false
    },
    {
      label: i18n.t('label.latest_V2'),
      value: true
    }
  ];

  const replicasDeploy = map(pipelineInstances, item => ({
    label: item.name,
    value: item.name
  }));

  const pipelineValidationError = useMemo(
    () =>
      validationPipeline?.filter?.(a => {
        const errors = a?.details.filter(detail => detail?.level === 'ERROR');
        return errors.length;
      }),
    [validationPipeline]
  );

  const closeDialog = () => {
    setShowConfirmDialog(false);
  };

  const confirmDeploy = value => {
    setShowConfirmDialog(false);

    props.find({
      realm: props.realm,
      environment: value.environment,
      search: {
        projectId: value.projectId,
        projectName: value?.projectName
      }
    });
    props.onCloseClick();

    const params = {
      projectId: value.projectId,
      refreshInterval: searchParams.get('refreshInterval') || '5'
    };

    const viewValue = searchParams.get('view');
    if (viewValue) {
      params.view = viewValue;
    }

    const pipelineNameValue = searchParams.get('pipelineName');
    if (pipelineNameValue != null && pipelineNameValue !== undefined) {
      params.pipelineName = pipelineNameValue;
    }

    setSearchParams(params);
  };

  return {
    ...props,
    ...actions,
    isPipelineEngine,
    versionMinor,
    optionVersionEngine,
    configurations,
    sliderProps,
    validationPipeline,
    validationLicense,
    configurationLastDeploy,
    replicasDeploy,
    trigger,
    triggers,
    triggerSource,
    pipeline,
    pipelineValidationError,
    licenses: pipeline?.licenseTotal,
    form,
    deployment: {
      ...deployment,
      licenseStatus: deployment?.pipeline?.licenseStatus
    },
    openPromote,
    closePromote,
    newDeployment,
    pipelineToPromoteConfirm,
    loadingPromote,
    texts: texts[deployType],
    showConfirmDialog,
    closeDialog,
    newDeploy,
    confirmDeploy,
    allowConcurrentScheduling: pipeline?.triggerSpec?.concurrentScheduling,
    isOpenAdvancedSettings,
    showInactiveTriggersExperience,
    loadingReplicaLimits,
    pipelineRedeploy,
    versionManuallyChanged,
    onChangecooldownPeriod: actions.onChangecooldownPeriod,
    isBetaEngineV3Eligible,
    isBetaEngineV3Enabled
  };
});

export default enhancer;
